import { User, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ContactCard } from "./contacts/ContactCard";
import { ContactListItem } from "./contacts/ContactListItem";
import { Contact } from "./contacts/types";
import { useTranslation } from "react-i18next";
import { useMultiSelect } from "@/hooks/useMultiSelect";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useState } from "react";
import { MultiSelectActions } from "@/components/common/MultiSelectActions";

interface ContactsListProps {
  contacts: Contact[];
  loading: boolean;
  viewMode: 'list' | 'card';
  companyId: string;
  onAddContact: () => void;
  onViewContact: (contact: Contact) => void;
  onEditContact: (contact: Contact) => void;
  onDeleteContacts: (contactIds: string[]) => Promise<void>;
}

export const ContactsList = ({ 
  contacts, 
  loading, 
  viewMode,
  companyId,
  onAddContact,
  onViewContact,
  onEditContact,
  onDeleteContacts
}: ContactsListProps) => {
  const { t } = useTranslation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const {
    isMultiSelectMode,
    toggleMultiSelect,
    toggleItemSelection,
    handleDeleteSelected,
    isSelected,
    selectedCount
  } = useMultiSelect(contacts, onDeleteContacts);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-gray-200 rounded-sm"></div>
        ))}
      </div>
    );
  }
  
  if (contacts.length === 0) {
    return (
      <div className="text-center py-8 border rounded-sm">
        <User className="h-8 w-8 text-gray-400 mx-auto" />
        <p className="mt-2 text-gray-500">{t("contacts.empty")}</p>
        <Button variant="outline" className="mt-3" onClick={onAddContact}>
          <Plus className="h-4 w-4 mr-2" />
          {t("contacts.add")}
        </Button>
      </div>
    );
  }
  
  return (
    <>
      <div className="mb-4">
        <MultiSelectActions
          isMultiSelectMode={isMultiSelectMode}
          onToggleMultiSelect={toggleMultiSelect}
          onDeleteSelected={() => setShowDeleteDialog(true)}
          selectedCount={selectedCount}
        />
      </div>

      <div className={viewMode === 'card' ? "grid grid-cols-1 md:grid-cols-2 gap-4" : "space-y-3"}>
        {contacts.map((contact) => (
          viewMode === 'card' ? (
            <div key={contact.id} className="relative">
              {isMultiSelectMode && (
                <div className="absolute top-2 right-2 z-10">
                  <input
                    type="checkbox"
                    checked={isSelected(contact.id)}
                    onChange={() => toggleItemSelection(contact.id)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                </div>
              )}
              <ContactCard 
                contact={contact} 
                onView={onViewContact} 
                onEdit={onEditContact} 
              />
            </div>
          ) : (
            <div key={contact.id} className="relative">
              {isMultiSelectMode && (
                <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10">
                  <input
                    type="checkbox"
                    checked={isSelected(contact.id)}
                    onChange={() => toggleItemSelection(contact.id)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                </div>
              )}
              <ContactListItem 
                contact={contact} 
                onView={onViewContact} 
                onEdit={onEditContact} 
              />
            </div>
          )
        ))}
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("common.deleteConfirmation", { count: selectedCount })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("common.deleteWarning")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleDeleteSelected();
                setShowDeleteDialog(false);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
